/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import * as React from "react";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "@ef/feature-core";
import { User, isAuthenticated } from "@ef/backend-core";
import { ping, startLogout } from "@ef/feature-login";

export type TimeoutMessageComponentProps = {
    readonly user: User;
    readonly interval?: number;
    readonly warnBefore?: number;
    readonly logoutAfter?: number;
}

function now() {
    return (new Date()).getTime();
}

// TODO. Implement logging - and remove commented console.logs

/**
 * Displays a message to warn before logout
 */
export default function TimeoutMessageComponent({ 
    user,
    interval = 20,
    warnBefore = 60,
    logoutAfter = 600,
}: TimeoutMessageComponentProps) {
    const [ lastActivity, setActivity ] = React.useState(now());
    const [ lastPing, setPing ] = React.useState(now());
    const [ tick, setTick ] = React.useState(now());

    const dispatch = useAppDispatch();

    const resetTimer = React.useCallback(() => {
        //console.log("Activity");
        setActivity(now());
    }, [ setActivity ]);

    const timetologout = logoutAfter + Math.floor((lastActivity - tick) / 1000); 

    // Subscribe to time-Tick
    React.useEffect(() => {
        const handle = setInterval(() => {
            //console.log("Tick");
            setTick(now());
        }, 1000);
        return () => clearInterval(handle);
    }, [ setTick ]);

    // Subscribe al activity handlers
    React.useEffect(() => {
        // eslint-disable-next-line no-console
        //console.log("TimeoutMessage: Register event listeners");
        document.addEventListener("mousemove", resetTimer, false);
        document.addEventListener("mousedown", resetTimer, false);
        document.addEventListener("keydown", resetTimer, false);
        document.addEventListener("DOMMouseScroll", resetTimer, false);
        document.addEventListener("mousewheel", resetTimer, false);
        document.addEventListener("touchmove", resetTimer, false);
        document.addEventListener("MSPointerMove", resetTimer, false);
    
        return () => {
            // eslint-disable-next-line no-console
            //console.log("TimeoutMessage: Remove event listeners");
            document.removeEventListener("mousemove", resetTimer, false);
            document.removeEventListener("mousedown", resetTimer, false);
            document.removeEventListener("keydown", resetTimer, false);
            document.removeEventListener("DOMMouseScroll", resetTimer, false);
            document.removeEventListener("mousewheel", resetTimer, false);
            document.removeEventListener("touchmove", resetTimer, false);
            document.removeEventListener("MSPointerMove", resetTimer, false);
        };
    }, [ resetTimer ]);

    // Trigger Backend Ping
    React.useEffect(() => {
        if (!isAuthenticated(user)) {
            // Nothing to do for not autheticated user
            return;
        }
        if ((tick - lastPing) > interval * 1000) {
            //console.log("Should ping");
            if (lastPing <= lastActivity + logoutAfter * 1000) {
                //console.log("Do ping");
                setPing(now());
                // Will eventually fail - we do not care. Store will be updated anyway
                dispatch(ping(user));
            }
        }
    }, [ interval, tick, lastPing, lastActivity, setPing, logoutAfter, dispatch, user ]);

    React.useEffect(() => {
        if (timetologout < 1) {
            dispatch(startLogout());
        }
    }, [ timetologout, dispatch ]);

    //console.log("Render", {
    //    lastPing, tick, lastActivity, timetologout
    //});
   
    if (isAuthenticated(user) && (timetologout < warnBefore)) {
        return (
            <Dialog open={true}>
                <DialogContent>
                    <Typography>Sie werden in {timetologout} Sekunden abgemeldet.</Typography>
                </DialogContent>
            </Dialog>
        );
    } else {
        return null;
    }
}
